import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Activity, ShieldCheck, FileDown, BookOpen, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { HeroShowcase } from "@/components/HeroShowcase";

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
            <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">RIF Master</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="header-login-btn">Sign in</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-blue-900 hover:bg-blue-800" data-testid="header-register-btn">Get lifetime access</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="eyebrow mb-4">Clinical decision support · Reproductive immunology</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Precision <span className="text-blue-900">RIF / RPL</span>: The Complete Clinical Workflow
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Customized, evidence-graded workflows translating global standards (ASRM, ESHRE, ASRI, RCOG) and complex
              immunology (KIR–HLA, Th1/Th2, autoimmune) into actionable, dosed regimens.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="bg-blue-900 hover:bg-blue-800 h-12 px-6" data-testid="hero-cta-register">
                Unlock lifetime access <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
              </Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="h-12 px-6" data-testid="hero-cta-login">
                I already have an account
              </Button></Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck className="h-4 w-4 text-teal-700" strokeWidth={1.75} />
              One-time payment · Lifetime updates · UPI accepted
            </div>
          </div>

          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="absolute -inset-3 bg-blue-900/5 rounded-md" />
              <div className="relative">
                <HeroShowcase />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              icon: Activity,
              title: "6-step clinical workflow",
              body: "Baseline → embryo & sperm → uterus → immunology → rare & systemic → dosed customised plan. Zero ambiguity.",
            },
            {
              icon: FileDown,
              title: "Patient-ready PDF export",
              body: "Every protocol prints to a clean, referable PDF for the patient chart.",
            },
            {
              icon: Sparkles,
              title: "Deep clinical review (AI-assisted)",
              body: (
                <>
                  Powered by Claude Sonnet 5, grounded on ASRM · ESHRE · ASRI · RCOG · ACOG. Delivers, for every case:
                  <ol className="mt-2 space-y-0.5 list-decimal pl-4 text-xs text-slate-600">
                    <li>Case snapshot</li>
                    <li>Working classification</li>
                    <li>Missing data to collect</li>
                    <li>Priority problems — issue, why it matters, evidence, next action</li>
                    <li>Personalised management plan with rationale &amp; risks</li>
                    <li>Add-ons and controversies</li>
                    <li>Red flags &amp; referrals</li>
                    <li>Patient counselling summary</li>
                  </ol>
                </>
              ),
            },
            {
              icon: BookOpen,
              title: "Curated research library",
              body: "ESHRE ’23, ASRM, KIR/HLA, ERA, CE and TB references at one click.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="border border-slate-200 bg-white p-6 rounded-md">
              <Icon className="h-6 w-6 text-blue-900" strokeWidth={1.5} />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{title}</h3>
              <div className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="eyebrow">Pricing</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900">One payment. Lifetime access.</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            No subscriptions, no renewals. Pay once and use the full RIF Master workflow, library and PDF export forever.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-6 text-left">
            <div className="border border-slate-200 rounded-md p-6">
              <div className="eyebrow">Includes</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {["Full 6-step interactive protocol generator","Deep AI clinical review (Claude Sonnet 5)","Dosed immunology & alloimmune plan","Unlimited patient case history","One-click PDF export with AI appendix","Curated research library","Lifetime updates"].map((x) => (
                  <li key={x} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-teal-700 mt-0.5" strokeWidth={1.75} /> {x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-2 border-blue-900 rounded-md p-6 bg-slate-50">
              <div className="eyebrow text-blue-900">Lifetime</div>
              <div className="mt-2 font-mono text-4xl font-bold text-slate-900">₹4,999</div>
              <div className="text-sm text-slate-500 mt-1">One-time payment via UPI</div>
              <Link to="/register" className="block mt-6">
                <Button className="w-full h-11 bg-blue-900 hover:bg-blue-800" data-testid="pricing-cta-register">
                  Create account & unlock
                </Button>
              </Link>
              <div className="mt-4 text-xs text-slate-500">Account verified manually by Dr. Shroff after payment.</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} RIF Master · Developed by Dr. Mona Shroff, MD</div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2" aria-label="Legal">
            <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/refund" className="hover:text-slate-900" data-testid="footer-refund-link">Refund &amp; Cancellation</Link>
            <Link to="/contact" className="hover:text-slate-900" data-testid="footer-contact-link">Contact Us</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
